//From https://github.com/ErrorPro/react-google-autocomplete
//Original license: MIT
//
// Notes: Pressing enter while autocomplete text input is still selected will be ignored
// Either 1) click on the selected option or 2) use arrow keys to select an option then enter.
//
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import Alert from 'elements/Alert'
import debounce from 'lodash/debounce'
import { BaseTheme, MenuItem, styled, TextField } from 'opensolar-ui'
import PropTypes from 'prop-types'
import React from 'react'
import { AutocompleteSuggestionList, withTranslate } from 'react-admin'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import appStorage from 'storage/appStorage'
import { lookupCountryIso2ByLocation } from 'util/lookupCountryIso2ByLocation'
import { parseGooglePlace, parsePostcoderPlace, reloadEntireApp } from 'util/misc'
import { looksLikeCoordinates, parseDecimalCoordinates } from 'util/parseDecimalCoordinates'
import postcoderLookup from 'util/postcoder'

const placeFieldsList = [
  'address_component',
  'adr_address',
  'business_status',
  'formatted_address',
  'geometry',
  'place_id',
  'types',
  'url',
  'utc_offset_minutes',
]

const PLACE_SERVICE_STATUS = {
  INVALID_REQUEST: 'This request was invalid',
  OK: 'The response contains a valid result',
  OVER_QUERY_LIMIT: 'The webpage has gone over its request quota',
  REQUEST_DENIED: 'The webpage is not allowed to use the PlacesService',
  UNKNOWN_ERROR:
    'The PlacesService request could not be processed due to a server error. The request may succeed if you try again',
  ZERO_RESULTS: 'No result was found for this request',
}

const defaultStatusState = {
  selectedFromDropDown: false,
  hasZeroPredictions: false,
  errorStatus: '',
}

const HelperMsgWrapper = styled('div')({
  position: 'absolute',
  width: '100%',
  left: 0,
  top: 50,
  zIndex: 50,
})

const StyledField = styled(TextField)({
  width: '100%',
  background: 'inherit',
  '& .OSUI-InputBase-root': {
    background: BaseTheme.palette.white,
  },
})

export class AddressAutocomplete extends React.Component {
  static propTypes = {
    onPlaceSelected: PropTypes.func,
    types: PropTypes.array,
    componentRestrictions: PropTypes.object,
    bounds: PropTypes.object,
    isWithErrorMessage: PropTypes.func,
    autoFocus: PropTypes.bool,
    enableCoordinateSearch: PropTypes.bool,
  }

  constructor(props) {
    super(props)
    this.service = null
    this.unmounted = false
    this.inputEl = React.createRef()
    this.state = {
      // useDefault: true,
      sessionToken: null,
      searchText: '',
      showSuggestionList: false,
      loadingPostcoder: false,
      predictions: null,
      mapsLibraryReady: false,
      inputElementReady: false,
      addressSearchReady: false,
      showPostcoderButton: false,
      showPostcoderResults: false,
      postCoderAddresses: [],
      coordinateParseStatus: null,
      ...defaultStatusState,
    }

    this.getPlaceSuggestions = debounce(this.getPlaceSuggestions, 500)
  }

  componentDidMount() {
    this.loadMapsLibrary()
  }

  componentDidUpdate(prevProps) {
    const { mapsLibraryReady, inputElementReady, addressSearchReady } = this.state
    if (mapsLibraryReady && inputElementReady && !addressSearchReady) {
      this.setupAddressSearch()
      this.setState({ addressSearchReady: true })
    }
  }

  componentWillUnmount() {
    this.unmounted = true
  }

  loadMapsLibrary() {
    if (!window.loadMapsLibrary) {
      this.setState({ initialError: true })
      console.log('Warning: AddressAutocomplete: window.loadMapsLibrary not set')
      return
    }

    const _this = this
    window
      .loadMapsLibrary('GoogleTop', this.props.api_key_google)
      .then(function () {
        if (_this.unmounted) return
        _this.setState({ mapsLibraryReady: true, initialError: false })
      })
      .catch((e) => _this.setState({ initialError: true }))
  }

  setupAddressSearch() {
    const { types = ['(cities)'], componentRestrictions, bounds, defaultValue } = this.props
    const config = {
      types: types,
      bounds: bounds,
      // Only basic fields are included, not premium/expensive Contact and Atmosphere fields.
      // Basic fields omitted: icon, name, photo, plus_code, utc_offset (soon deprecated), vicinity, website
      fields: placeFieldsList,
    }

    if (componentRestrictions) {
      config.componentRestrictions = componentRestrictions
    }

    this.service = new window.google.maps.places.AutocompleteService()
    this.placesService = new window.google.maps.places.PlacesService(this.inputEl.current)

    if (defaultValue) {
      this.setState({
        searchText: defaultValue,
        showSuggestionList: false,
        showPostcoderButton: false,
      })
    }
  }

  handleLibraryReload() {
    this.setState({ initialError: false })
    this.loadMapsLibrary()
  }

  setInputElement(element) {
    this.inputEl.current = element
    if (!this.state.inputElementReady && !!element) {
      // prevent infinite loop by not setting state unnecessarily
      this.setState({ inputElementReady: true })
    }
  }

  getSessionToken = () => {
    const { sessionToken } = this.state
    if (sessionToken) {
      return sessionToken
    } else {
      const newSessionToken = new window.google.maps.places.AutocompleteSessionToken()
      this.setState({ sessionToken: newSessionToken })
      return newSessionToken
    }
  }

  getPlaceSuggestions(newValue) {
    const { types = ['(cities)'], componentRestrictions } = this.props
    const sessionToken = this.getSessionToken()
    this.service.getPlacePredictions(
      { input: newValue, types: types, sessionToken, componentRestrictions },
      this.handleSuggestions.bind(this)
    )
  }

  getCoordinateParseStatus(searchText) {
    if (!this.props.enableCoordinateSearch || !searchText?.trim()) {
      return null
    }
    if (parseDecimalCoordinates(searchText)) {
      return 'valid'
    }
    if (looksLikeCoordinates(searchText)) {
      return 'invalid'
    }
    return null
  }

  handleInputChange(event) {
    const newValue = event.target.value || ''
    const coordinateParseStatus = this.getCoordinateParseStatus(newValue)
    if (coordinateParseStatus != null) {
      this.setState({
        searchText: newValue,
        selectedFromDropDown: false,
        showSuggestionList: true,
        coordinateParseStatus,
        predictions: null,
        hasZeroPredictions: false,
      })
      return
    }
    this.setState({
      searchText: newValue,
      selectedFromDropDown: false,
      showSuggestionList: true,
      coordinateParseStatus,
    })
    if (newValue && newValue.length > 3 && this.service) {
      this.getPlaceSuggestions(newValue)
    } else {
      this.setState({ predictions: null })
    }
  }

  handleCoordinateKeyDown(event) {
    if (!this.props.enableCoordinateSearch || event.key !== 'Enter') {
      return false
    }

    const { searchText } = this.state
    const coordinates = parseDecimalCoordinates(searchText)
    if (coordinates) {
      event.preventDefault()
      void this.submitCoordinates(coordinates)
      return true
    }

    if (looksLikeCoordinates(searchText)) {
      event.preventDefault()
      return true
    }

    return false
  }

  async submitCoordinates({ lat, lon }) {
    if (!this.props.onPlaceSelected) {
      return
    }

    const detectedCountryIso2 = await lookupCountryIso2ByLocation(lat, lon)
    if (!detectedCountryIso2) {
      this.setState({
        coordinateParseStatus: 'country_lookup_failed',
        showSuggestionList: true,
      })
      return
    }

    const placeAsFields = {
      lat,
      lon,
      address: `${lat}, ${lon}`,
      country_iso2: detectedCountryIso2,
    }

    this.setState({
      searchText: placeAsFields.address,
      selectedFromDropDown: true,
      showSuggestionList: false,
      coordinateParseStatus: 'valid',
      sessionToken: null,
      ...defaultStatusState,
    })
    this.props.onPlaceSelected(placeAsFields)
    this.toggleAutocompleteList(false)
  }

  handleSuggestions(predictions, status) {
    this.setState({ predictions })
    this.setState({ ...defaultStatusState })
    if (status === 'OK') {
      // do nothing
    } else if (status === 'ZERO_RESULTS') {
      this.setState({ hasZeroPredictions: true })
    } else {
      this.setState({ errorStatus: status })
    }
  }

  handlePlaceDetail(place) {
    if (place && place.address_components) {
      this.setState({ searchText: place['formatted_address'], selectedFromDropDown: true, sessionToken: null })
      this.props.onPlaceSelected(parseGooglePlace(place))
    } else {
      // Place not valid, perhaps enter was pressed in the textfield, ignore it.
      console.warn('onSelected ignored, place invalid', place)
    }
    // this.setState({ searchText: suggestion.description })
    this.toggleAutocompleteList(false)
  }

  onSelected(suggestion) {
    if (this.state.predictions && this.state.predictions.indexOf(suggestion) !== -1) {
      // Google suggestion
      const { sessionToken } = this.state
      if (this.props.onPlaceSelected && suggestion.place_id) {
        this.placesService.getDetails(
          {
            fields: placeFieldsList,
            placeId: suggestion.place_id,
            sessionToken: sessionToken,
          },
          this.handlePlaceDetail.bind(this)
        )
      }
    } else if (this.state.postCoderAddresses.indexOf(suggestion) !== -1) {
      // Postcoder suggestion
      if (this.props.onPlaceSelected) {
        this.props.onPlaceSelected(parsePostcoderPlace(suggestion))
      }
    } else {
      console.warn("Couldn't determine the type of suggestion")
    }
  }

  getCoordinateHelperMessage() {
    const { translate } = this.props
    const { coordinateParseStatus } = this.state

    if (coordinateParseStatus === 'invalid') {
      return {
        severity: 'warning',
        message: translate(
          'Please enter latitude and longitude separated by a comma or space, for example: 12.9716, 77.5946'
        ),
      }
    }
    if (coordinateParseStatus === 'valid') {
      return {
        severity: 'info',
        message: translate('Coordinates detected. Press Enter to create a project at this location.'),
      }
    }
    if (coordinateParseStatus === 'country_lookup_failed') {
      return {
        severity: 'warning',
        message: translate(
          'Could not detect country for these coordinates. Please try again or enter an address instead.'
        ),
      }
    }
    return null
  }

  getAutocompleteWarningMessage() {
    const { translate } = this.props
    const { searchText, selectedFromDropDown, hasZeroPredictions, loadingPostcoder, coordinateParseStatus } = this.state
    let result = ''
    if (searchText.length === 0) return result

    if (coordinateParseStatus != null) {
      return ''
    }

    if (searchText.length < 4) {
      result = 'Please enter an address that is at least 4 characters long'
    } else if (hasZeroPredictions) {
      result = 'Address Not Found'
    } else if (loadingPostcoder) {
      result = 'Loading Results'
    } else if (
      searchText.length > 3 &&
      !selectedFromDropDown &&
      this.state.predictions &&
      this.state.predictions.length > 0
    ) {
      result = 'Please select from the drop down list'
    }
    return translate(result)
  }

  getAutocompleteErrorMessage() {
    const { errorStatus } = this.state
    const { translate } = this.props
    let result = ''
    if (errorStatus) {
      result = PLACE_SERVICE_STATUS[errorStatus]
    }
    return translate(result)
  }

  renderHelperMessage() {
    const coordinateHelperMessage = this.getCoordinateHelperMessage()
    const autocompleteWarningMessage = this.getAutocompleteWarningMessage()
    const autocompleteErrorMessage = this.getAutocompleteErrorMessage()
    const { translate, isWithErrorMessage } = this?.props
    if (coordinateHelperMessage) {
      if (isWithErrorMessage) isWithErrorMessage(coordinateHelperMessage.severity !== 'info')
      return <Alert severity={coordinateHelperMessage.severity}>{coordinateHelperMessage.message}</Alert>
    }
    if (autocompleteErrorMessage) {
      if (isWithErrorMessage) isWithErrorMessage(true)
      return (
        <Alert severity="error">
          {autocompleteErrorMessage}
          <button
            style={{ marginLeft: 20 }}
            onClick={(e) => {
              e.preventDefault()
              reloadEntireApp()
            }}
          >
            {translate('Reload')}
          </button>
        </Alert>
      )
    } else if (autocompleteWarningMessage) {
      if (isWithErrorMessage) isWithErrorMessage(true)
      return <Alert severity="warning">{autocompleteWarningMessage}</Alert>
    } else {
      if (isWithErrorMessage) isWithErrorMessage(false)
      return null
    }
  }

  toggleAutocompleteList = (value) => {
    this.setState({
      showSuggestionList: value,
    })
  }

  async handleSearchAddresses() {
    const token = appStorage.getToken()
    const orgId = appStorage.getOrgId()
    const serchText = this.state.searchText

    this.setState({ loadingPostcoder: true })
    postcoderLookup(token, orgId, serchText)
      .then((data) => {
        if (data.length === 0) {
          this.setState({ loadingPostcoder: false, showSuggestionList: false, hasZeroPredictions: true })
        } else {
          this.setState({
            loadingPostcoder: false,
            postCoderAddresses: data,
            showPostcoderButton: false,
            showPostcoderResults: true,
          })
        }
      })
      .catch((e) => {
        console.error('Failed to lookup address via postcoder: ', e)
        this.setState({ loadingPostcoder: false, errorStatus: e.status, showSuggestionList: false })
      })
  }

  render() {
    //Added basePath purely so it won't be added as property to input, which would throw errors
    const {
      style = { width: 'calc(100% - 20px)' },
      onPlaceSelected,
      types,
      componentRestrictions,
      bounds,
      basePath,
      initializeForm,
      input,
      record,
      api_key_google,
      theme,
      defaultValue,
      translate,
      orgCountry,
      addressLookUp,
      className,
      renderHelperMessage,
      autoFocus = true,

      ...rest
    } = this.props
    const { showSuggestionList, predictions, searchText, loadingPostcoder, mapsLibraryReady } = this.state
    const showList = !!(showSuggestionList && !loadingPostcoder && predictions && predictions.length > 0)
    const proUxStyle = {
      width: '100%',
    }

    return mapsLibraryReady ? (
      <div style={{ position: this.props.disableRelative ? undefined : 'relative' }}>
        {showList && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
            }}
            onClick={() => this.setState({ showSuggestionList: false, showPostcoderButton: false })}
          />
        )}
        {this.props.isProUx ? (
          <input
            onKeyDown={(event) => {
              if (this.handleCoordinateKeyDown(event)) {
                return
              }
              if (event.key === 'Enter') {
                event.preventDefault()
              }
            }}
            onChange={this.handleInputChange.bind(this)}
            id="Address-Autocomplete-Input"
            className={`${
              this.props.isProjectPage
                ? 'proux-address-autocomplete project-page-autocomplete'
                : 'proux-address-autocomplete'
            } ${className}`}
            style={proUxStyle}
            ref={this.setInputElement.bind(this)}
            {...sanitizeRestProps(rest)}
            onFocus={(e) => {
              if (window.disableChromeAutoComplete) {
                window.disableChromeAutoComplete()
              }
              this.setState({
                showSuggestionList: true,
                showPostcoderButton: this.props.addressLookUp,
                showPostcoderResults: false,
              })
            }}
            value={this.state.searchText}
            autoFocus={autoFocus}
          />
        ) : this.props.isCashFlowUx ? (
          <StyledField
            onKeyDown={(event) => {
              if (this.handleCoordinateKeyDown(event)) {
                return
              }
              if (event.key === 'Enter') {
                event.preventDefault()
              }
            }}
            size="small"
            variant="outlined"
            label={this.props.label}
            onChange={this.handleInputChange.bind(this)}
            id="Address-Autocomplete-Input"
            className={`${className} OpenSolarGoogleAutocomplete`}
            inputRef={this.setInputElement.bind(this)}
            {...sanitizeRestProps(rest)}
            onFocus={(e) => {
              if (window.disableChromeAutoComplete) {
                window.disableChromeAutoComplete()
              }
              this.setState({
                showSuggestionList: true,
                showPostcoderButton: this.props.addressLookUp,
                showPostcoderResults: false,
              })
            }}
            onBlur={(e) => {
              const { searchText, predictions } = this.state
              if (searchText) {
                const normalizedSearchText = searchText.trim().toLowerCase()
                const addressBreakdown = normalizedSearchText.split(',').map((part) => part.trim())

                // Check if default value is a complete address
                // We expect at least 4 parts for a complete address: street, city, state, and postal code
                if (addressBreakdown.length >= 4) {
                  // Check for a match in predictions / dropdown options
                  if (Array.isArray(predictions) && predictions.length > 0) {
                    const isMatched = predictions.some((prediction) => {
                      const matchedParts = addressBreakdown.filter((part) =>
                        prediction.description.toLowerCase().includes(part.trim())
                      )
                      return matchedParts.length >= 3
                    })

                    this.setState({
                      selectedFromDropDown: isMatched,
                      showSuggestionList: !isMatched,
                    })
                  }
                }
              }
            }}
            value={this.state.searchText}
            autoFocus={autoFocus}
          />
        ) : (
          <input
            onKeyDown={(event) => {
              if (this.handleCoordinateKeyDown(event)) {
                return
              }
              if (event.key === 'Enter') {
                event.preventDefault()
              }
            }}
            onChange={this.handleInputChange.bind(this)}
            id="Address-Autocomplete-Input"
            className={`${className} OpenSolarGoogleAutocomplete`}
            style={style}
            ref={this.setInputElement.bind(this)}
            {...sanitizeRestProps(rest)}
            onFocus={(e) => {
              if (window.disableChromeAutoComplete) {
                window.disableChromeAutoComplete()
              }
              this.setState({
                showSuggestionList: true,
                showPostcoderButton: this.props.addressLookUp,
                showPostcoderResults: false,
              })
            }}
            value={this.state.searchText}
            autoFocus={autoFocus}
          />
        )}
        <HelperMsgWrapper>{this.renderHelperMessage()}</HelperMsgWrapper>
        <AutocompleteSuggestionList
          PaperStyle={{ zIndex: 1000, borderRadius: 2, marginTop: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}
          isOpen={showList}
          inputEl={this.inputEl.current}
        >
          {predictions &&
            !this.state.showPostcoderResults &&
            predictions.map((suggestion, index) => (
              <SuggestionItem
                key={suggestion.place_id}
                index={index}
                onSelected={this.onSelected.bind(this)}
                suggestion={suggestion}
                searchText={searchText}
              />
            ))}
          {this.props.addressLookUp && orgCountry === 'GB' && (
            <div>
              {this.state.showPostcoderButton && (
                <MenuItem
                  style={{
                    fontSize: 14,
                    borderTop: '1px solid #d9d9d9',
                  }}
                  onClick={this.handleSearchAddresses.bind(this)}
                >
                  Search for "{this.state.searchText}" using AddressBase...
                </MenuItem>
              )}
              {this.state.postCoderAddresses &&
                this.state.showPostcoderResults &&
                this.state.postCoderAddresses.map((suggestion) => (
                  <SuggestionItem
                    key={suggestion.premise}
                    searchText={searchText}
                    onSelected={this.onSelected.bind(this)}
                    suggestion={suggestion}
                  />
                ))}
            </div>
          )}

          {!this.state.showPostcoderResults && (
            <img
              alt="google-logo"
              style={{
                height: 10,
                float: 'right',
                margin: 5,
              }}
              src={`${window.PUBLIC_URL}/images/powered_by_google.png`}
            />
          )}
        </AutocompleteSuggestionList>
      </div>
    ) : this.state.initialError ? (
      <Alert severity="error">
        {translate('Maps library not loaded, click button to reload')}...
        <button style={{ marginLeft: 20 }} onClick={this.handleLibraryReload.bind(this)}>
          {translate('Reload')}
        </button>
      </Alert>
    ) : (
      <p>{translate('Loading Google Maps Address Search')}...</p>
    )
  }
}
export const addressAutocompleteMapStateToProps = (state) => {
  const org = orgSelectors.getOrg(state)
  return {
    theme: state.theme,
    orgCountry: authSelectors.getCurrentOrgCountry(state),
    addressLookUp: org?.enable_uk_address_lookup && org?.enable_address_base_lookup,
  }
}
export default compose(withTranslate, connect(addressAutocompleteMapStateToProps))(AddressAutocomplete)

export const SuggestionItem = ({ suggestion, searchText, onSelected }) => {
  let matches = []
  let parts

  if (suggestion.description) {
    matches = match(suggestion.description, searchText)
    parts = parse(suggestion.description, matches)
  }

  if (suggestion.summaryline) {
    matches = match(suggestion.summaryline, searchText)
    parts = parse(suggestion.summaryline, matches)
  }

  if (suggestion.service_address) {
    matches = match(suggestion.service_address, searchText)
    parts = parse(suggestion.service_address, matches)
  }

  return (
    <MenuItem onClick={() => onSelected(suggestion)} style={{ fontSize: 14, borderTop: '1px solid #d9d9d9' }}>
      <div>
        {parts.map((part, index) => {
          return part.highlight ? (
            <span key={index} style={{ fontWeight: 600 }}>
              {part.text}
            </span>
          ) : (
            <strong key={index} style={{ fontWeight: 300 }}>
              {part.text}
            </strong>
          )
        })}
      </div>
    </MenuItem>
  )
}

const sanitizeRestProps = ({ dispatch, ...rest }) => rest
